import { useMemo } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Box, Button, Stack, Text, Tooltip } from "@mantine/core";
import { resolveUploadsUrl } from "@/lib/uploads";
import { api } from "@/lib/api";
import { showNotification } from "@mantine/notifications";
import type { SupportKnowledgeArticleRow } from "@/types/api";

type Props = {
  content: string;
  contentFormat?: string | null;
  plain?: boolean;
  relatedArticles?: SupportKnowledgeArticleRow[];
  onArticleClick?: (article: SupportKnowledgeArticleRow) => void;
  variableDefinitions?: Record<string, string>;
  projectId?: string | null;
};

function normalizeArticleTitle(title: string): string {
  return title.trim().replace(/\s+/g, " ").toLowerCase();
}

function referenceTitleFromLine(line: string): string | null {
  const trimmed = line.trim();
  const bullet = trimmed.match(/^[-•]\s+(.+)$/);
  const next = trimmed.match(/^Далее:\s*(.+)$/i);
  const value = (bullet?.[1] || next?.[1] || "").trim();
  if (!value || value === "—") return null;
  return value.replace(/[.;]+$/g, "").trim();
}

function buildArticleLookup(
  articles?: SupportKnowledgeArticleRow[],
): Map<string, SupportKnowledgeArticleRow> {
  const lookup = new Map<string, SupportKnowledgeArticleRow>();
  for (const article of articles || []) {
    const key = normalizeArticleTitle(article.title);
    if (key && !lookup.has(key)) lookup.set(key, article);
  }
  return lookup;
}

function looksLikeHtml(content: string): boolean {
  return /<\s*(p|br|ul|ol|li|h[1-4]|img|strong|em|blockquote|pre|div)\b/i.test(
    content,
  );
}

function normalizeVariableKey(raw: string): string {
  return raw
    .trim()
    .replace(/^\[+|\]+$/g, "")
    .toLowerCase();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildVariableDefinitionMap(
  variableDefinitions?: Record<string, string>,
): Map<string, { key: string; definition: string }> {
  const map = new Map<string, { key: string; definition: string }>();
  for (const [rawKey, rawDefinition] of Object.entries(
    variableDefinitions || {},
  )) {
    const key = rawKey.trim().replace(/^\[+|\]+$/g, "");
    const definition = String(rawDefinition || "").trim();
    if (key && definition)
      map.set(normalizeVariableKey(key), { key, definition });
  }
  return map;
}

function variableRegex(
  variableMap: Map<string, { key: string; definition: string }>,
): RegExp | null {
  const keys = Array.from(variableMap.values())
    .map((item) => item.key)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);
  if (!keys.length) return null;
  const alternatives = keys.join("|");
  return new RegExp(
    `\\[(${alternatives})\\]|(^|[^\\p{L}\\p{N}_-])(${alternatives})(?=$|[^\\p{L}\\p{N}_-])`,
    "giu",
  );
}

function variableDefinitionTitle(key: string, definition: string): string {
  return `${key}: ${definition}`;
}

function renderTextWithVariableTooltips(
  text: string,
  variableMap: Map<string, { key: string; definition: string }>,
): ReactNode[] | string {
  const regex = variableRegex(variableMap);
  if (!regex) return text;

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const full = match[0];
    const bracketKey = match[1];
    const prefix = match[2] || "";
    const bareKey = match[3];
    const key = bracketKey || bareKey;
    const variable = variableMap.get(normalizeVariableKey(key || ""));
    if (!variable) continue;

    const variableStart = match.index + prefix.length;
    if (variableStart > lastIndex)
      parts.push(text.slice(lastIndex, variableStart));
    const label = text.slice(variableStart, match.index + full.length);
    parts.push(
      <Tooltip
        key={`${variable.key}-${variableStart}`}
        label={variableDefinitionTitle(variable.key, variable.definition)}
        withArrow
        multiline
      >
        <span className="kb-variable-token">{label}</span>
      </Tooltip>,
    );
    lastIndex = match.index + full.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
}

function resolveHtmlAssetUrls(
  html: string,
  variableDefinitions?: Record<string, string>,
): string {
  if (!html.trim()) return "";
  if (typeof DOMParser === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    // Older saves/Excel imports lost custom hrefs. Restore title-based links
    // only inside the explicitly marked reference sections.
    let referenceSection = false;
    for (const element of Array.from(doc.body.querySelectorAll("h1,h2,h3,h4,h5,h6,p,li"))) {
      const sectionTitle = (element.textContent || "").trim().replace(/^##\s*/, "");
      if (/^H[1-6]$/.test(element.tagName) || /^(Связанные статьи|Следующий шаг)\s*:?$/i.test(sectionTitle)) {
        referenceSection = /^(Связанные статьи|Следующий шаг)\s*:?$/i.test(sectionTitle);
        continue;
      }
      if (!referenceSection) continue;
      if (element.tagName === "LI" && element.querySelector("p")) continue;
      const entries = [element];
      for (const entry of entries) {
        const existing = entry.querySelector("a");
        if (existing?.getAttribute("href")) continue;
        const title = (existing?.textContent || entry.textContent || "").replace(/^(Далее:|[-•])\s*/i, "").replace(/[.;]+$/, "").trim();
        if (!title || title === "—") continue;
        const anchor = existing || doc.createElement("a");
        anchor.setAttribute("href", `kb-article:${encodeURIComponent(title)}`);
        if (!existing) { anchor.textContent = title; entry.replaceChildren(anchor); }
      }
    }
    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("data:") || src.startsWith("http")) return;
      img.setAttribute("src", resolveUploadsUrl(src));
    });
    doc.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto:"))
        return;
      if (href.startsWith("kb-article:")) return;
      if (href.startsWith("/uploads/") || href.startsWith("uploads/")) {
        a.setAttribute("href", resolveUploadsUrl(href));
      }
    });
    const variableMap = buildVariableDefinitionMap(variableDefinitions);
    const regex = variableRegex(variableMap);
    if (regex) {
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
      const textNodes: Text[] = [];
      let node = walker.nextNode();
      while (node) {
        const parent = node.parentElement;
        if (
          parent &&
          !["SCRIPT", "STYLE", "TEXTAREA"].includes(parent.tagName)
        ) {
          textNodes.push(node as Text);
        }
        node = walker.nextNode();
      }
      for (const textNode of textNodes) {
        const text = textNode.nodeValue || "";
        regex.lastIndex = 0;
        let match: RegExpExecArray | null;
        let lastIndex = 0;
        const fragment = doc.createDocumentFragment();
        let replaced = false;
        while ((match = regex.exec(text)) !== null) {
          const full = match[0];
          const bracketKey = match[1];
          const prefix = match[2] || "";
          const bareKey = match[3];
          const key = bracketKey || bareKey;
          const variable = variableMap.get(normalizeVariableKey(key || ""));
          if (!variable) continue;
          const variableStart = match.index + prefix.length;
          if (variableStart > lastIndex)
            fragment.append(text.slice(lastIndex, variableStart));
          const span = doc.createElement("span");
          span.className = "kb-variable-token";
          span.setAttribute(
            "title",
            variableDefinitionTitle(variable.key, variable.definition),
          );
          span.textContent = text.slice(
            variableStart,
            match.index + full.length,
          );
          fragment.append(span);
          lastIndex = match.index + full.length;
          replaced = true;
        }
        if (!replaced) continue;
        if (lastIndex < text.length) fragment.append(text.slice(lastIndex));
        textNode.replaceWith(fragment);
      }
    }
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

export function stripKnowledgeContent(
  content: string,
  contentFormat?: string | null,
): string {
  const raw = content || "";
  const isHtml =
    contentFormat === "html" ||
    (contentFormat !== "plain" && looksLikeHtml(raw));
  if (!isHtml) return raw.trim();
  if (typeof DOMParser === "undefined") {
    return raw
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  } catch {
    return raw;
  }
}

export function KnowledgeArticleContent({
  content,
  contentFormat,
  plain = false,
  relatedArticles,
  onArticleClick,
  variableDefinitions,
  projectId,
}: Props) {
  const isHtml = useMemo(() => {
    if (plain) return false;
    return (
      contentFormat === "html" ||
      (contentFormat !== "plain" && looksLikeHtml(content))
    );
  }, [content, contentFormat, plain]);

  const html = useMemo(
    () => (isHtml ? resolveHtmlAssetUrls(content, variableDefinitions) : ""),
    [content, isHtml, variableDefinitions],
  );
  const articleLookup = useMemo(
    () => buildArticleLookup(relatedArticles),
    [relatedArticles],
  );
  const variableMap = useMemo(
    () => buildVariableDefinitionMap(variableDefinitions),
    [variableDefinitions],
  );
  const openReference = async (title: string) => {
    try {
      const article = await api.supportKnowledgeResolve(title, projectId);
      onArticleClick?.(article);
    } catch (error) {
      showNotification({ title: "Не удалось открыть связанный материал", message: error instanceof Error ? error.message : "Материал удалён или недоступен", color: "red" });
    }
  };
  const handleHtmlClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onArticleClick) return;
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest(
      'a[href^="kb-article:"]',
    ) as HTMLAnchorElement | null;
    if (!anchor) return;
    event.preventDefault();
    const raw = anchor.getAttribute("href")?.replace(/^kb-article:/, "") || "";
    let title = raw;
    try { title = decodeURIComponent(raw); } catch { /* tolerate legacy unescaped titles */ }
    void openReference(title);
  };

  if (!content?.trim()) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  if (!isHtml) {
    let referenceSection = false;
    return (
      <Stack gap="xs">
        {content.split(/\n/).map((line, index) => {
          const heading = line.match(/^##\s+(.+)$/);
          if (heading) {
            referenceSection = /^(Связанные статьи|Следующий шаг)\s*:?$/i.test(heading[1].trim());
            return (
              <Text
                key={`${index}-${line}`}
                fw={700}
                size="sm"
                mt={index === 0 ? 0 : "sm"}
              >
                {renderTextWithVariableTooltips(heading[1].trim(), variableMap)}
              </Text>
            );
          }

          const referenceTitle = referenceTitleFromLine(line) || (referenceSection ? line.trim().replace(/[.;]+$/, "") : null);
          const linkedArticle = referenceTitle
            ? articleLookup.get(normalizeArticleTitle(referenceTitle))
            : null;
          if (referenceTitle && referenceTitle !== "—" && (linkedArticle || referenceSection || /^Далее:/i.test(line.trim())) && onArticleClick) {
            return (
              <Box key={`${index}-${line}`}>
                <Button
                  size="compact-sm"
                  variant="light"
                  onClick={() => void openReference(referenceTitle)}
                >
                  {line.trim().startsWith("-") || line.trim().startsWith("•")
                    ? referenceTitle
                    : `Далее: ${referenceTitle}`}
                </Button>
              </Box>
            );
          }

          if (!line.trim()) return <Box key={`${index}-${line}`} h={2} />;

          return (
            <Text
              key={`${index}-${line}`}
              size="sm"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {renderTextWithVariableTooltips(line, variableMap)}
            </Text>
          );
        })}
      </Stack>
    );
  }

  return (
    <Box
      className="kb-article-content"
      style={{ fontSize: "var(--mantine-font-size-sm)", lineHeight: 1.6 }}
      onClick={handleHtmlClick}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
